// ** react imports
import React from 'react';

// ** router imports
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// ** toaster imports
import { toast } from 'react-toastify';

// ** validation imports
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks
import { useSetNewPassword } from '../hooks/apis/useUsers';

// ** components imports
import LoadingButton from '../components/common/Buttons/LoadingButton';
import PasswordInput from '../components/common/Inputs/PasswordInput';

// ** images imports
import AuthImage from '../images/auth-image.jpg';
import Logo from '../images/Logo.png';

// ** enums imports
import { BUTTON_THEMES } from '../enums/common';

const SetNewPassword = props => {
  // props
  const {} = props;

  // hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  // custom hooks
  const { isSetNewPasswordLoading, setNewPassword } = useSetNewPassword();

  // validation hooks
  const validationSchema = yup.object().shape({
    oldPassword: yup
      .string()
      .required('Old password is required')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/,
        'Old password at least 8 characters, one special character, one uppercase, one lowercase, and one number'
      ),
    newPassword: yup
      .string()
      .required('New password is required')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[a-zA-Z\d!@#$%^&*]{8,}$/,
        'New password at least 8 characters, one special character, one uppercase, one lowercase, and one number'
      ),
  });

  // react hook form
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  // handler
  const onSubmit = async data => {
    if (!token) {
      return toast.error('Token must required.');
    }

    const response = await setNewPassword({ ...data, token });
    if (response.data.success) {
      navigate('/auth/signin');
    }
  };

  return (
    <main className="bg-white dark:bg-gray-900">
      <div className="relative md:flex">
        <div className="md:w-1/2">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            {/* Header */}
            <div className="flex-1">
              <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                {/* Logo */}
                <Link className="block" to="/">
                  <div className="w-10 h-10">
                    <img
                      src={Logo}
                      alt="Multi Tax"
                      className="h-full w-full object-contain"
                    />
                  </div>
                </Link>
              </div>
            </div>

            <div className="max-w-sm mx-auto w-full px-4 py-8">
              <h1 className="text-3xl text-gray-800 dark:text-gray-100 font-bold mb-6">
                Set your new password
              </h1>
              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="space-y-4">
                  <div>
                    <PasswordInput
                      label="Old password*"
                      name="oldPassword"
                      register={register}
                      errors={errors.oldPassword?.message}
                    />
                  </div>

                  <div>
                    <PasswordInput
                      label="New password*"
                      name="newPassword"
                      register={register}
                      errors={errors.newPassword?.message}
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <LoadingButton
                    theme={BUTTON_THEMES.PRIMARY}
                    isLoading={isSetNewPasswordLoading}
                    type="submit"
                  >
                    Set Password
                  </LoadingButton>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Image */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2"
          aria-hidden="true"
        >
          <img
            className="object-cover object-center w-full h-full"
            src={AuthImage}
            width="760"
            height="1024"
            alt="Authentication"
          />
        </div>
      </div>
    </main>
  );
};

export default SetNewPassword;
